/**
 * ISBN Validation and Utilities Module
 *
 * This module provides comprehensive ISBN validation, normalization, formatting,
 * and conversion utilities for both ISBN-10 and ISBN-13 formats.
 *
 * Target: 300-400 LOC
 * Module: #27 (Second frontend module)
 * Dependencies: Module #26 (web/config.ts)
 *
 * Features:
 * - ISBN-10 and ISBN-13 validation with checksum verification
 * - Normalization and cleaning (strip hyphens, spaces, etc.)
 * - Conversion between ISBN-10 and ISBN-13
 * - Formatting for display or API calls
 * - Batch operations (validate, deduplicate, filter)
 * - TypeScript strict mode with full type safety
 *
 * @module web/lib/isbn
 */

// =============================================================================
// Types & Interfaces
// =============================================================================

/**
 * ISBN format types
 */
export type ISBNFormat = "ISBN-10" | "ISBN-13" | "INVALID";

/**
 * Error codes for ISBN validation failures
 */
export type ISBNErrorCode =
  | "EMPTY_ISBN"
  | "INVALID_LENGTH"
  | "INVALID_CHARACTERS"
  | "INVALID_CHECKSUM"
  | "CONVERSION_ERROR"
  | "UNKNOWN_ERROR";

/**
 * ISBN validation error details
 */
export interface ISBNError {
  /** Machine-readable error code */
  code: ISBNErrorCode;

  /** Human-readable error message */
  message: string;

  /** Original input that caused the error */
  input?: string;
}

/**
 * Result of ISBN validation operation
 */
export interface ISBNValidationResult {
  /** Whether the ISBN is valid */
  isValid: boolean;

  /** Detected format (ISBN-10, ISBN-13, or INVALID) */
  format: ISBNFormat;

  /** Normalized ISBN (digits only, no hyphens/spaces) */
  normalized: string;

  /** Error details if validation failed */
  error?: ISBNError;

  /** Original input before normalization */
  original?: string;
}

/**
 * Options for formatting ISBNs
 */
export interface ISBNFormatOptions {
  /** Include hyphens in formatted output */
  includeHyphens: boolean;

  /** Formatting style */
  style: "standard" | "compact";
}

/**
 * Result of batch ISBN validation
 */
export interface ISBNBatchResult {
  /** Total number of ISBNs processed */
  total: number;

  /** Number of valid ISBNs */
  validCount: number;

  /** Number of invalid ISBNs */
  invalidCount: number;

  /** Valid ISBNs (normalized) */
  valid: string[];

  /** Invalid ISBNs with error details */
  invalid: Array<{
    isbn: string;
    error: ISBNError;
  }>;

  /** Duplicate ISBNs that were removed */
  duplicates: string[];
}

// =============================================================================
// Constants
// =============================================================================

/**
 * ISBN-13 prefix for books (978 = books, 979 = books/music)
 */
export const ISBN_13_PREFIX_BOOKS = "978";
export const ISBN_13_PREFIX_MUSIC = "979";

/**
 * Valid lengths for ISBN formats
 */
export const ISBN_10_LENGTH = 10;
export const ISBN_13_LENGTH = 13;

/**
 * Regular expressions for ISBN validation
 */
const ISBN_10_REGEX = /^[0-9]{9}[0-9X]$/;
const ISBN_13_REGEX = /^[0-9]{13}$/;
const ISBN_CLEAN_REGEX = /[\s-]/g;
const ISBN_VALID_CHARS_REGEX = /^[0-9X\s-]+$/i;

/**
 * Default formatting options
 */
const DEFAULT_FORMAT_OPTIONS: ISBNFormatOptions = {
  includeHyphens: false,
  style: "compact",
};

// =============================================================================
// Error Messages
// =============================================================================

const ERROR_MESSAGES: Record<ISBNErrorCode, string> = {
  EMPTY_ISBN: "ISBN cannot be empty",
  INVALID_LENGTH: "ISBN must be 10 or 13 digits",
  INVALID_CHARACTERS: "ISBN contains invalid characters",
  INVALID_CHECKSUM: "ISBN checksum validation failed",
  CONVERSION_ERROR: "Unable to convert ISBN format",
  UNKNOWN_ERROR: "Unknown validation error",
};

/**
 * Create an ISBN error object
 */
function createError(code: ISBNErrorCode, input?: string): ISBNError {
  return {
    code,
    message: ERROR_MESSAGES[code],
    input,
  };
}

// =============================================================================
// Core Validation Functions (to be implemented in Phase 3 & 4)
// =============================================================================

/**
 * Validate an ISBN (auto-detect format)
 *
 * Automatically detects whether the input is ISBN-10 or ISBN-13 and validates accordingly.
 * Returns detailed validation results including format, normalized value, and error details.
 *
 * @param isbn - ISBN string to validate
 * @returns Validation result with format and error details
 *
 * @example
 * ```typescript
 * const result = validateISBN('978-0-596-52068-7');
 * if (result.isValid) {
 *   console.log(`Valid ${result.format}: ${result.normalized}`);
 * } else {
 *   console.error(result.error?.message);
 * }
 * ```
 */
export function validateISBN(isbn: string): ISBNValidationResult {
  const normalized = normalizeISBN(isbn);
  const original = isbn;

  // Check for empty input
  if (!normalized) {
    return {
      isValid: false,
      format: "INVALID",
      normalized: "",
      original,
      error: createError("EMPTY_ISBN", original),
    };
  }

  // Check for valid characters
  if (!ISBN_VALID_CHARS_REGEX.test(original)) {
    return {
      isValid: false,
      format: "INVALID",
      normalized,
      original,
      error: createError("INVALID_CHARACTERS", original),
    };
  }

  // Determine format by length
  if (normalized.length === ISBN_13_LENGTH) {
    const isValid = validateISBN13(normalized);
    return {
      isValid,
      format: isValid ? "ISBN-13" : "INVALID",
      normalized,
      original,
      error: isValid ? undefined : createError("INVALID_CHECKSUM", original),
    };
  } else if (normalized.length === ISBN_10_LENGTH) {
    const isValid = validateISBN10(normalized);
    return {
      isValid,
      format: isValid ? "ISBN-10" : "INVALID",
      normalized,
      original,
      error: isValid ? undefined : createError("INVALID_CHECKSUM", original),
    };
  } else {
    return {
      isValid: false,
      format: "INVALID",
      normalized,
      original,
      error: createError("INVALID_LENGTH", original),
    };
  }
}

/**
 * Validate ISBN-13 format
 *
 * @param isbn - ISBN-13 string to validate
 * @returns True if valid ISBN-13
 *
 * @example
 * ```typescript
 * validateISBN13('9780596520687');  // => true
 * validateISBN13('978-0-596-52068-7');  // => true (handles formatting)
 * validateISBN13('9780596520688');  // => false (bad checksum)
 * validateISBN13('0596520689');     // => false (ISBN-10, not ISBN-13)
 * ```
 */
export function validateISBN13(isbn: string): boolean {
  // Normalize the input
  const normalized = normalizeISBN(isbn);

  // Check length
  if (normalized.length !== ISBN_13_LENGTH) {
    return false;
  }

  // Check format (must be all digits)
  if (!ISBN_13_REGEX.test(normalized)) {
    return false;
  }

  // Verify checksum
  return verifyISBN13Checksum(normalized);
}

/**
 * Validate ISBN-10 format
 *
 * @param isbn - ISBN-10 string to validate
 * @returns True if valid ISBN-10
 *
 * @example
 * ```typescript
 * validateISBN10('0596520689');     // => true
 * validateISBN10('043942089X');     // => true (with X check digit)
 * validateISBN10('0596520688');     // => false (invalid checksum)
 * validateISBN10('978-0596520687'); // => false (ISBN-13)
 * ```
 */
export function validateISBN10(isbn: string): boolean {
  const normalized = normalizeISBN(isbn);

  // Check length
  if (normalized.length !== ISBN_10_LENGTH) {
    return false;
  }

  // Check format (9 digits + 1 digit or X)
  if (!ISBN_10_REGEX.test(normalized)) {
    return false;
  }

  // Verify checksum
  return verifyISBN10Checksum(normalized);
}

/**
 * Check if a string is a valid ISBN (any format)
 *
 * @param isbn - String to check
 * @returns True if valid ISBN-10 or ISBN-13
 */
export function isISBN(isbn: string): boolean {
  const result = validateISBN(isbn);
  return result.isValid;
}

/**
 * Get the format of an ISBN
 *
 * @param isbn - ISBN string
 * @returns Format type (ISBN-10, ISBN-13, or INVALID)
 */
export function getISBNFormat(isbn: string): ISBNFormat {
  const result = validateISBN(isbn);
  return result.format;
}

// =============================================================================
// Normalization Functions (to be implemented in Phase 2)
// =============================================================================

/**
 * Normalize an ISBN by removing hyphens, spaces, and converting to uppercase
 *
 * @param isbn - ISBN string to normalize
 * @returns Normalized ISBN (digits only, uppercase)
 *
 * @example
 * ```typescript
 * normalizeISBN('978-0-596-52068-7');  // => '9780596520687'
 * normalizeISBN('978 0 596 52068 7');  // => '9780596520687'
 * normalizeISBN('0-596-52068-X');      // => '059652068X'
 * normalizeISBN('  978-0-596-52068-7  '); // => '9780596520687'
 * ```
 */
export function normalizeISBN(isbn: string): string {
  if (!isbn) {
    return "";
  }

  // Trim whitespace, remove hyphens and spaces, convert to uppercase
  return isbn.trim().replace(ISBN_CLEAN_REGEX, "").toUpperCase();
}

/**
 * Strip formatting characters from ISBN
 *
 * @param isbn - ISBN string
 * @returns ISBN with hyphens and spaces removed
 *
 * @example
 * ```typescript
 * stripISBN('978-0-596-52068-7');  // => '9780596520687'
 * stripISBN('0-596-52068-X');      // => '059652068X'
 * ```
 */
export function stripISBN(isbn: string): string {
  if (!isbn) {
    return "";
  }

  // Remove hyphens and spaces only
  return isbn.replace(ISBN_CLEAN_REGEX, "");
}

/**
 * Clean and prepare ISBN for validation
 *
 * @param isbn - ISBN string to clean
 * @returns Cleaned ISBN string
 *
 * @example
 * ```typescript
 * cleanISBN('  978-0-596-52068-7  ');  // => '9780596520687'
 * cleanISBN('ISBN: 978-0-596-52068-7'); // => '9780596520687'
 * ```
 */
export function cleanISBN(isbn: string): string {
  if (!isbn) {
    return "";
  }

  // Trim, remove common prefixes, normalize
  let cleaned = isbn.trim().toUpperCase();

  // Remove common ISBN prefixes
  cleaned = cleaned.replace(/^ISBN[:\s-]*/i, "");

  // Remove all non-ISBN characters (keep only digits and X)
  cleaned = cleaned.replace(/[^0-9X]/g, "");

  return cleaned;
}

// =============================================================================
// Checksum Functions (to be implemented in Phase 3 & 4)
// =============================================================================

/**
 * Calculate ISBN-13 checksum digit
 *
 * Algorithm:
 * - Take first 12 digits
 * - Multiply digits at even positions (0-indexed) by 1
 * - Multiply digits at odd positions (0-indexed) by 3
 * - Sum all products
 * - Checksum = (10 - (sum mod 10)) mod 10
 *
 * @param isbn - First 12 digits of ISBN-13 (or full 13 digits)
 * @returns Checksum digit (0-9)
 *
 * @example
 * ```typescript
 * calculateISBN13Checksum('978059652068');  // => 7
 * calculateISBN13Checksum('9780596520687'); // => 7 (ignores existing checksum)
 * ```
 */
export function calculateISBN13Checksum(isbn: string): number {
  // Normalize and take first 12 digits
  const normalized = normalizeISBN(isbn);
  const first12 = normalized.substring(0, 12);

  if (first12.length !== 12) {
    throw new Error("ISBN must have at least 12 digits");
  }

  // Calculate weighted sum
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(first12[i], 10);
    // Even positions (0, 2, 4...) multiply by 1
    // Odd positions (1, 3, 5...) multiply by 3
    const weight = i % 2 === 0 ? 1 : 3;
    sum += digit * weight;
  }

  // Calculate checksum
  const checksum = (10 - (sum % 10)) % 10;
  return checksum;
}

/**
 * Calculate ISBN-10 checksum digit
 *
 * Algorithm:
 * - Multiply each of the first 9 digits by descending weights (10, 9, 8, ..., 2)
 * - Sum all products
 * - Calculate (11 - (sum mod 11)) mod 11
 * - If result is 10, return 'X', otherwise return the digit
 *
 * @param isbn - First 9 digits of ISBN-10 (or complete ISBN-10)
 * @returns Checksum digit (0-9 or 'X')
 *
 * @example
 * ```typescript
 * calculateISBN10Checksum('030640615');  // => '2'
 * calculateISBN10Checksum('043942089');  // => 'X'
 * ```
 */
export function calculateISBN10Checksum(isbn: string): string {
  const normalized = normalizeISBN(isbn);

  // Take only first 9 digits
  const digits = normalized.substring(0, 9);

  if (digits.length !== 9 || !/^\d{9}$/.test(digits)) {
    throw new Error("ISBN-10 checksum requires 9 digits");
  }

  // Calculate weighted sum: digit[i] * (10 - i)
  // Weights: 10, 9, 8, 7, 6, 5, 4, 3, 2
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const weight = 10 - i;
    sum += parseInt(digits[i], 10) * weight;
  }

  // Calculate checksum: (11 - (sum mod 11)) mod 11
  const checksum = (11 - (sum % 11)) % 11;

  // Return 'X' for 10, otherwise the digit
  return checksum === 10 ? "X" : checksum.toString();
}

/**
 * Verify ISBN-13 checksum
 *
 * @param isbn - Complete ISBN-13 (13 digits)
 * @returns True if checksum is valid
 *
 * @example
 * ```typescript
 * verifyISBN13Checksum('9780596520687');  // => true
 * verifyISBN13Checksum('9780596520688');  // => false
 * ```
 */
export function verifyISBN13Checksum(isbn: string): boolean {
  const normalized = normalizeISBN(isbn);

  if (normalized.length !== ISBN_13_LENGTH) {
    return false;
  }

  // Calculate what the checksum should be
  const calculatedChecksum = calculateISBN13Checksum(normalized);

  // Get the actual checksum (last digit)
  const actualChecksum = parseInt(normalized[12], 10);

  return calculatedChecksum === actualChecksum;
}

/**
 * Verify ISBN-10 checksum
 *
 * @param isbn - Complete ISBN-10
 * @returns True if checksum is valid
 *
 * @example
 * ```typescript
 * verifyISBN10Checksum('0596520689');  // => true
 * verifyISBN10Checksum('043942089X');  // => true
 * verifyISBN10Checksum('0596520688');  // => false
 * ```
 */
export function verifyISBN10Checksum(isbn: string): boolean {
  const normalized = normalizeISBN(isbn);

  if (normalized.length !== ISBN_10_LENGTH) {
    return false;
  }

  // Calculate what the checksum should be
  const calculatedChecksum = calculateISBN10Checksum(normalized);

  // Get the actual checksum (last character)
  const actualChecksum = normalized[9];

  return calculatedChecksum === actualChecksum;
}

// =============================================================================
// Conversion Functions (to be implemented in Phase 5)
// =============================================================================

/**
 * Convert ISBN-10 to ISBN-13
 *
 * Algorithm:
 * - Add "978" prefix
 * - Take first 9 digits of ISBN-10
 * - Calculate new ISBN-13 checksum for the 12 digits
 * - Append checksum to create 13-digit ISBN
 *
 * @param isbn10 - Valid ISBN-10
 * @returns ISBN-13 equivalent
 * @throws Error if ISBN-10 is invalid
 *
 * @example
 * ```typescript
 * convertISBN10to13('0596520689');  // => '9780596520687'
 * convertISBN10to13('043942089X');  // => '9780439420891'
 * ```
 */
export function convertISBN10to13(isbn10: string): string {
  const normalized = normalizeISBN(isbn10);

  // Validate ISBN-10
  if (!validateISBN10(normalized)) {
    throw new Error("Invalid ISBN-10: cannot convert to ISBN-13");
  }

  // Take first 9 digits (remove ISBN-10 checksum)
  const first9 = normalized.substring(0, 9);

  // Add 978 prefix
  const isbn13WithoutChecksum = ISBN_13_PREFIX_BOOKS + first9;

  // Calculate ISBN-13 checksum
  const checksum = calculateISBN13Checksum(isbn13WithoutChecksum);

  return isbn13WithoutChecksum + checksum.toString();
}

/**
 * Convert ISBN-13 to ISBN-10
 *
 * Note: Only ISBN-13s with 978 prefix can be converted to ISBN-10.
 * ISBNs with 979 prefix cannot be represented in ISBN-10 format.
 *
 * Algorithm:
 * - Check for 978 prefix
 * - Extract 9 digits after prefix
 * - Calculate ISBN-10 checksum
 * - Return 10-digit ISBN
 *
 * @param isbn13 - Valid ISBN-13
 * @returns ISBN-10 equivalent, or null if not convertible (non-978 prefix)
 *
 * @example
 * ```typescript
 * convertISBN13to10('9780596520687');  // => '0596520689'
 * convertISBN13to10('9790000000000');  // => null (979 prefix not convertible)
 * ```
 */
export function convertISBN13to10(isbn13: string): string | null {
  const normalized = normalizeISBN(isbn13);

  // Validate ISBN-13
  if (!validateISBN13(normalized)) {
    throw new Error("Invalid ISBN-13: cannot convert to ISBN-10");
  }

  // Check if it starts with 978 (only 978-prefix ISBNs can be converted)
  if (!normalized.startsWith(ISBN_13_PREFIX_BOOKS)) {
    return null;
  }

  // Extract 9 digits after 978 prefix
  const first9 = normalized.substring(3, 12);

  // Calculate ISBN-10 checksum
  const checksum = calculateISBN10Checksum(first9);

  return first9 + checksum;
}

/**
 * Convert any ISBN to ISBN-13 format
 *
 * Convenience function that detects the format and converts if necessary.
 * If already ISBN-13, returns normalized version.
 * If ISBN-10, converts to ISBN-13.
 *
 * @param isbn - ISBN-10 or ISBN-13
 * @returns ISBN-13 format
 * @throws Error if ISBN is invalid
 *
 * @example
 * ```typescript
 * toISBN13('0596520689');      // => '9780596520687'
 * toISBN13('9780596520687');   // => '9780596520687'
 * toISBN13('0-596-52068-9');   // => '9780596520687'
 * ```
 */
export function toISBN13(isbn: string): string {
  const normalized = normalizeISBN(isbn);

  // Check if it's already ISBN-13
  if (normalized.length === ISBN_13_LENGTH) {
    if (!validateISBN13(normalized)) {
      throw new Error("Invalid ISBN-13");
    }
    return normalized;
  }

  // If ISBN-10, convert to ISBN-13
  if (normalized.length === ISBN_10_LENGTH) {
    return convertISBN10to13(normalized);
  }

  throw new Error("Invalid ISBN: must be 10 or 13 digits");
}

// =============================================================================
// Formatting Functions (to be implemented in Phase 6)
// =============================================================================

/**
 * Format an ISBN for display
 *
 * Auto-detects ISBN-10 or ISBN-13 and formats accordingly.
 *
 * @param isbn - ISBN to format (ISBN-10 or ISBN-13)
 * @param options - Formatting options
 * @returns Formatted ISBN string
 *
 * @example
 * ```typescript
 * formatISBN('9780596520687');                          // => '9780596520687'
 * formatISBN('9780596520687', { includeHyphens: true }); // => '978-0-596-52068-7'
 * formatISBN('0596520689', { includeHyphens: true });    // => '0-596-52068-9'
 * ```
 */
export function formatISBN(
  isbn: string,
  options?: Partial<ISBNFormatOptions>,
): string {
  const opts = { ...DEFAULT_FORMAT_OPTIONS, ...options };
  const normalized = normalizeISBN(isbn);

  // Detect format and delegate to specific formatter
  if (normalized.length === ISBN_13_LENGTH) {
    return formatISBN13(normalized, opts.includeHyphens);
  } else if (normalized.length === ISBN_10_LENGTH) {
    return formatISBN10(normalized, opts.includeHyphens);
  } else {
    throw new Error("Invalid ISBN: must be 10 or 13 digits");
  }
}

/**
 * Format ISBN-13 with optional hyphens
 *
 * Note: This uses a simple formatting pattern (978-0-XXX-XXXXX-X) for display purposes.
 * Official ISBN hyphenation requires group/publisher range data.
 *
 * @param isbn - ISBN-13 to format
 * @param includeHyphens - Whether to include hyphens
 * @returns Formatted ISBN-13
 *
 * @example
 * ```typescript
 * formatISBN13('9780596520687', false);  // => '9780596520687'
 * formatISBN13('9780596520687', true);   // => '978-0-596-52068-7'
 * formatISBN13('978-0-596-52068-7', true); // => '978-0-596-52068-7'
 * ```
 */
export function formatISBN13(
  isbn: string,
  includeHyphens: boolean = false,
): string {
  const normalized = normalizeISBN(isbn);

  if (!validateISBN13(normalized)) {
    throw new Error("Invalid ISBN-13: cannot format");
  }

  if (!includeHyphens) {
    return normalized;
  }

  // Simple formatting: 978-0-XXX-XXXXX-X (3-1-3-5-1)
  const prefix = normalized.substring(0, 3);
  const group = normalized.substring(3, 4);
  const publisher = normalized.substring(4, 7);
  const title = normalized.substring(7, 12);
  const checksum = normalized.substring(12, 13);

  return `${prefix}-${group}-${publisher}-${title}-${checksum}`;
}

/**
 * Format ISBN-10 with optional hyphens
 *
 * Note: This uses a simple formatting pattern (0-XXX-XXXXX-X) for display purposes.
 * Official ISBN hyphenation requires group/publisher range data.
 *
 * @param isbn - ISBN-10 to format
 * @param includeHyphens - Whether to include hyphens
 * @returns Formatted ISBN-10
 *
 * @example
 * ```typescript
 * formatISBN10('0596520689', false);  // => '0596520689'
 * formatISBN10('0596520689', true);   // => '0-596-52068-9'
 * formatISBN10('043942089X', true);   // => '0-439-42089-X'
 * ```
 */
export function formatISBN10(
  isbn: string,
  includeHyphens: boolean = false,
): string {
  const normalized = normalizeISBN(isbn);

  if (!validateISBN10(normalized)) {
    throw new Error("Invalid ISBN-10: cannot format");
  }

  if (!includeHyphens) {
    return normalized;
  }

  // Simple formatting: 0-XXX-XXXXX-X (1-3-5-1)
  const group = normalized.substring(0, 1);
  const publisher = normalized.substring(1, 4);
  const title = normalized.substring(4, 9);
  const checksum = normalized.substring(9, 10);

  return `${group}-${publisher}-${title}-${checksum}`;
}

// =============================================================================
// Batch Operations (to be implemented in Phase 7)
// =============================================================================

/**
 * Validate a batch of ISBNs
 *
 * Processes an array of ISBN strings and returns detailed results including
 * valid/invalid counts, normalized valid ISBNs, invalid ISBNs with errors,
 * and duplicates found.
 *
 * @param isbns - Array of ISBN strings
 * @returns Batch validation result
 *
 * @example
 * ```typescript
 * const result = validateBatch(['978-0-596-52068-7', '0596520689', 'invalid']);
 * // {
 * //   total: 3,
 * //   validCount: 2,
 * //   invalidCount: 1,
 * //   valid: ['9780596520687', '0596520689'],
 * //   invalid: [{ isbn: 'invalid', error: {...} }],
 * //   duplicates: []
 * // }
 * ```
 */
export function validateBatch(isbns: string[]): ISBNBatchResult {
  const valid: string[] = [];
  const invalid: Array<{ isbn: string; error: ISBNError }> = [];
  const seen = new Set<string>();
  const duplicates: string[] = [];

  for (const isbn of isbns) {
    const result = validateISBN(isbn);

    if (result.isValid) {
      const normalized = result.normalized;

      // Check for duplicates
      if (seen.has(normalized)) {
        if (!duplicates.includes(normalized)) {
          duplicates.push(normalized);
        }
      } else {
        seen.add(normalized);
        valid.push(normalized);
      }
    } else {
      invalid.push({
        isbn,
        error: result.error || createError("UNKNOWN_ERROR", isbn),
      });
    }
  }

  return {
    total: isbns.length,
    validCount: valid.length,
    invalidCount: invalid.length,
    valid,
    invalid,
    duplicates,
  };
}

/**
 * Filter valid ISBNs from a batch
 *
 * Returns only the valid, normalized ISBNs from the input array.
 * Duplicates are preserved in the output.
 *
 * @param isbns - Array of ISBN strings
 * @returns Array of valid, normalized ISBNs
 *
 * @example
 * ```typescript
 * filterValid(['978-0-596-52068-7', 'invalid', '0596520689']);
 * // => ['9780596520687', '0596520689']
 * ```
 */
export function filterValid(isbns: string[]): string[] {
  return isbns
    .map((isbn) => validateISBN(isbn))
    .filter((result) => result.isValid)
    .map((result) => result.normalized);
}

/**
 * Filter invalid ISBNs from a batch
 *
 * Returns only the invalid ISBN strings from the input array.
 * Preserves the original format (not normalized).
 *
 * @param isbns - Array of ISBN strings
 * @returns Array of invalid ISBN strings
 *
 * @example
 * ```typescript
 * filterInvalid(['978-0-596-52068-7', 'invalid', '0596520688']);
 * // => ['invalid', '0596520688']
 * ```
 */
export function filterInvalid(isbns: string[]): string[] {
  return isbns.filter((isbn) => !validateISBN(isbn).isValid);
}

/**
 * Remove duplicate ISBNs from a batch (preserves order)
 *
 * Normalizes all ISBNs and removes duplicates, keeping the first occurrence.
 * Order is preserved. Invalid ISBNs are skipped.
 *
 * @param isbns - Array of ISBN strings
 * @returns Array with duplicates removed
 *
 * @example
 * ```typescript
 * deduplicateISBNs(['978-0-596-52068-7', '9780596520687', '0596520689']);
 * // => ['9780596520687', '0596520689']
 * // (first two are the same ISBN in different formats)
 * ```
 */
export function deduplicateISBNs(isbns: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const isbn of isbns) {
    const validation = validateISBN(isbn);

    if (validation.isValid) {
      const normalized = validation.normalized;

      if (!seen.has(normalized)) {
        seen.add(normalized);
        result.push(normalized);
      }
    }
  }

  return result;
}

/**
 * Normalize a batch of ISBNs
 *
 * Normalizes all ISBNs in the array (removes hyphens, spaces, uppercase).
 * Does not validate - simply applies normalization to all strings.
 *
 * @param isbns - Array of ISBN strings
 * @returns Array of normalized ISBNs
 *
 * @example
 * ```typescript
 * normalizeBatch(['978-0-596-52068-7', '0-596-52068-9', 'ISBN: 043942089X']);
 * // => ['9780596520687', '0596520689', '043942089X']
 * ```
 */
export function normalizeBatch(isbns: string[]): string[] {
  return isbns.map((isbn) => normalizeISBN(isbn));
}

// =============================================================================
// Module Exports Summary
// =============================================================================

/**
 * All exported functions organized by category:
 *
 * Validation:
 * - validateISBN, validateISBN13, validateISBN10, isISBN, getISBNFormat
 *
 * Normalization:
 * - normalizeISBN, stripISBN, cleanISBN
 *
 * Checksum:
 * - calculateISBN13Checksum, calculateISBN10Checksum
 * - verifyISBN13Checksum, verifyISBN10Checksum
 *
 * Conversion:
 * - convertISBN10to13, convertISBN13to10, toISBN13
 *
 * Formatting:
 * - formatISBN, formatISBN13, formatISBN10
 *
 * Batch Operations:
 * - validateBatch, filterValid, filterInvalid, deduplicateISBNs, normalizeBatch
 */
